'use client'

import * as React from 'react'
import { Tooltip as TooltipPrimitive } from 'radix-ui'

import { cn } from '@/lib/utils'

function TooltipProvider({
  delayDuration = 0,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Provider>) {
  return (
    <TooltipPrimitive.Provider
      data-slot="tooltip-provider"
      delayDuration={delayDuration}
      {...props}
    />
  )
}

function Tooltip({ ...props }: React.ComponentProps<typeof TooltipPrimitive.Root>) {
  return <TooltipPrimitive.Root data-slot="tooltip" {...props} />
}

function TooltipTrigger({
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Trigger>) {
  return <TooltipPrimitive.Trigger data-slot="tooltip-trigger" {...props} />
}

function TooltipContent({
  className,
  sideOffset = 0,
  children,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Content>) {
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        data-slot="tooltip-content"
        sideOffset={sideOffset}
        className={cn(
          'z-70 w-max max-w-[calc(100vw-2rem)] origin-(--radix-tooltip-content-transform-origin) animate-in rounded-md border bg-popover px-3 py-2 text-left text-xs text-popover-foreground shadow-md fade-in-0 zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 sm:max-w-96',
          className,
        )}
        {...props}
      >
        <div
          data-slot="tooltip-scroll-area"
          className="max-h-[calc(var(--radix-tooltip-content-available-height)-1rem-2px)] overflow-y-auto overscroll-contain"
        >
          {children}
        </div>
        <TooltipPrimitive.Arrow className="z-70 size-2.5 translate-y-[calc(-50%_-_2px)] rotate-45 rounded-[2px] bg-popover fill-popover" />
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  )
}

function TooltipText({ className, ...props }: React.ComponentProps<'p'>) {
  return (
    <p
      className={cn(
        'line-clamp-2 max-w-80 [overflow-wrap:anywhere] break-words',
        className,
      )}
      {...props}
    />
  )
}

function TooltipList({
  items,
  className,
}: {
  items: readonly string[]
  className?: string
}) {
  if (items.length === 0) return <span>—</span>

  return (
    <ul className={cn('grid min-w-0 gap-0.5', className)}>
      {items.map((item, index) => (
        <li key={`${item}-${index}`} className="[overflow-wrap:anywhere] break-words">
          {item}
        </li>
      ))}
    </ul>
  )
}

function TooltipLabeledList({
  rows,
  className,
}: {
  rows: ReadonlyArray<{ label: string; items: readonly string[] }>
  className?: string
}) {
  return (
    <dl
      className={cn(
        'grid grid-cols-[auto_minmax(0,1fr)] items-start gap-x-4 gap-y-1',
        className,
      )}
    >
      {rows.map(({ label, items }) => (
        <React.Fragment key={label}>
          <dt className="text-muted-foreground">{label}</dt>
          <dd className="min-w-0">
            <TooltipList items={items} />
          </dd>
        </React.Fragment>
      ))}
    </dl>
  )
}

export {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
  TooltipText,
  TooltipList,
  TooltipLabeledList,
}
